import React, { useState, useEffect, useCallback, useRef } from "react";
import { authFetch, API_BASE } from "../api";
import SearchBar from "./shared/SearchBar";
import "./shared/shared.css";
import "./WebhooksPage.css";

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 5000) return "just now";
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return new Date(ts).toLocaleTimeString();
}

function truncateJson(obj, maxLen = 120) {
  const str = typeof obj === "string" ? obj : JSON.stringify(obj);
  if (!str) return "—";
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + "...";
}

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="5" width="8" height="8" rx="1" />
    <path d="M9 5V2C9 1.45 8.55 1 8 1H2C1.45 1 1 1.45 1 2V8C1 8.55 1.45 9 2 9H5" />
  </svg>
);

function buildCurlCommand(url, payload) {
  const escaped = payload.replace(/'/g, "'\\''");
  return `curl -X POST \\\n  -H "Content-Type: application/json" \\\n  -d '${escaped}' \\\n  ${url}`;
}

export default function WebhooksPage({ integrations, cmsAuth }) {
  const [activity, setActivity] = useState([]);
  const [stats, setStats] = useState({ total: 0, successCount: 0, errorCount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [urlsOpen, setUrlsOpen] = useState(true);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [testPayload, setTestPayload] = useState('{\n  "message": "Hello from Forge Studio!",\n  "test": true\n}');
  const [testTarget, setTestTarget] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [curlExpandedName, setCurlExpandedName] = useState(null);
  const [inlineTestStatus, setInlineTestStatus] = useState({});
  const intervalRef = useRef(null);

  const webhookBase = API_BASE + "/webhooks/";

  const fetchActivity = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/webhooks/activity`);
      const data = await res.json();
      setActivity(data.entries || []);
      setStats({ total: data.total || 0, successCount: data.successCount || 0, errorCount: data.errorCount || 0 });
    } catch (err) {
      console.error("Failed to fetch webhook activity:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchActivity, 3000);
      return () => clearInterval(intervalRef.current);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [autoRefresh, fetchActivity]);

  const clearLog = async () => {
    if (!window.confirm("Clear all webhook activity?")) return;
    await authFetch(`${API_BASE}/webhooks/activity`, { method: "DELETE" });
    setActivity([]);
    setStats({ total: 0, successCount: 0, errorCount: 0 });
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  const resend = async (entry) => {
    try {
      await authFetch(`${API_BASE}/webhooks/${entry.integrationName}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry.body),
      });
      fetchActivity();
    } catch (err) {
      console.error("Resend failed:", err);
    }
  };

  const sendTest = async () => {
    if (!testTarget || testSending) return;
    setTestSending(true);
    try {
      const payload = JSON.parse(testPayload);
      await authFetch(`${API_BASE}/webhooks/${testTarget}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      fetchActivity();
    } catch (err) {
      alert("Invalid JSON or send failed: " + err.message);
    } finally {
      setTestSending(false);
    }
  };

  const sendInlineTest = async (integrationName) => {
    setInlineTestStatus((prev) => ({ ...prev, [integrationName]: "sending" }));
    try {
      const payload = JSON.parse(testPayload);
      await authFetch(`${API_BASE}/webhooks/${integrationName}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setInlineTestStatus((prev) => ({ ...prev, [integrationName]: "success" }));
      fetchActivity();
      setTimeout(() => setInlineTestStatus((prev) => ({ ...prev, [integrationName]: null })), 2000);
    } catch {
      setInlineTestStatus((prev) => ({ ...prev, [integrationName]: "error" }));
      setTimeout(() => setInlineTestStatus((prev) => ({ ...prev, [integrationName]: null })), 3000);
    }
  };

  const filtered = activity.filter((e) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      e.integrationName?.toLowerCase().includes(s) ||
      JSON.stringify(e.body)?.toLowerCase().includes(s)
    );
  });

  const runningApps = (integrations || []).filter((ig) => ig.state === "running");

  return (
    <div className="webhooks-page">
      <div className="page-header">
        <h2>Webhooks</h2>
        <div className="page-header-right">
          <span className="apps-stat">{stats.total} received</span>
          <span className="apps-stat apps-stat-running">{stats.successCount} success</span>
          <span className="apps-stat" style={{ color: stats.errorCount > 0 ? "var(--color-danger)" : "var(--color-text-faint)" }}>
            {stats.errorCount} errors
          </span>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <SearchBar value={search} onChange={setSearch} placeholder="Filter by app or payload..." />
        </div>
        <div className="page-toolbar-right">
          <label className="wh-auto-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            <span>Live</span>
          </label>
          <button className="wh-toolbar-btn" onClick={fetchActivity} title="Refresh">&#x21bb;</button>
          <button className="wh-toolbar-btn wh-toolbar-btn-danger" onClick={clearLog} title="Clear log">Clear</button>
        </div>
      </div>

      <div className="page-body">
        {/* Webhook URLs */}
        <div className="wh-section">
          <button className="wh-section-toggle" onClick={() => setUrlsOpen(!urlsOpen)}>
            <span className="wh-section-arrow">{urlsOpen ? "▾" : "▸"}</span>
            <span className="wh-section-title">Webhook URLs</span>
            <span className="wh-section-count">{runningApps.length} endpoints</span>
          </button>
          {urlsOpen && (
            <div className="wh-urls-list">
              {runningApps.length === 0 ? (
                <div className="wh-urls-empty">No running apps. Start an app to get its webhook URL.</div>
              ) : (
                runningApps.map((ig) => {
                  const url = webhookBase + ig.name;
                  const curlCmd = buildCurlCommand(url, testPayload);
                  const testStatus = inlineTestStatus[ig.name];
                  return (
                    <div key={ig.name} className="wh-url-item">
                      <div className="wh-url-row">
                        <span className="wh-url-name">{ig.name}</span>
                        <code className="wh-url-value">{url}</code>
                        <button
                          className="wh-url-copy"
                          onClick={() => copyUrl(url)}
                          title="Copy URL"
                        >
                          {copiedUrl === url ? "✓" : <CopyIcon />}
                        </button>
                        <button
                          className={`wh-url-action${curlExpandedName === ig.name ? " active" : ""}`}
                          onClick={() => setCurlExpandedName(curlExpandedName === ig.name ? null : ig.name)}
                          title="Show curl command"
                        >
                          curl
                        </button>
                        <button
                          className={`wh-url-action wh-url-test${testStatus === "success" ? " success" : ""}${testStatus === "error" ? " error" : ""}`}
                          onClick={() => sendInlineTest(ig.name)}
                          disabled={testStatus === "sending"}
                          title="Send test payload"
                        >
                          {testStatus === "sending" ? "..." : testStatus === "success" ? "✓" : testStatus === "error" ? "✗" : "Test"}
                        </button>
                      </div>
                      {curlExpandedName === ig.name && (
                        <div className="wh-curl-block">
                          <pre className="wh-curl-code">{curlCmd}</pre>
                          <button
                            className="wh-curl-copy"
                            onClick={() => copyUrl(curlCmd)}
                            title="Copy curl command"
                          >
                            {copiedUrl === curlCmd ? "✓" : <CopyIcon />}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Test sender */}
        <div className="wh-section">
          <button className="wh-section-toggle" onClick={() => setTestTarget(testTarget ? "" : (runningApps[0]?.name || ""))}>
            <span className="wh-section-arrow">{testTarget ? "▾" : "▸"}</span>
            <span className="wh-section-title">Send Test Payload</span>
          </button>
          {testTarget !== "" && (
            <div className="wh-test-panel">
              <div className="wh-test-row">
                <select
                  className="wh-test-select"
                  value={testTarget}
                  onChange={(e) => setTestTarget(e.target.value)}
                >
                  <option value="">Select app...</option>
                  {runningApps.map((ig) => (
                    <option key={ig.name} value={ig.name}>{ig.name}</option>
                  ))}
                </select>
                <button
                  className="wh-test-send"
                  onClick={sendTest}
                  disabled={!testTarget || testSending}
                >
                  {testSending ? "Sending..." : "Send"}
                </button>
              </div>
              <textarea
                className="wh-test-textarea"
                value={testPayload}
                onChange={(e) => setTestPayload(e.target.value)}
                rows={5}
                spellCheck={false}
              />
            </div>
          )}
        </div>

        {/* Activity log */}
        <div className="wh-section">
          <div className="wh-section-toggle" style={{ cursor: "default" }}>
            <span className="wh-section-title">Activity Log</span>
            <span className="wh-section-count">{filtered.length} entries</span>
          </div>

          {loading ? (
            <div className="page-empty">Loading activity...</div>
          ) : filtered.length === 0 ? (
            <div className="page-empty">
              {search ? `No activity matching "${search}"` : "No webhook activity yet. Send a POST to any webhook URL to see it here."}
            </div>
          ) : (
            <div className="wh-log">
              {filtered.map((entry) => {
                const isExpanded = expandedId === entry.id;
                const isError = entry.status >= 400 || entry.error;
                return (
                  <div key={entry.id} className={`wh-log-entry ${isError ? "error" : "success"}`}>
                    <div className="wh-log-row" onClick={() => setExpandedId(isExpanded ? null : entry.id)}>
                      <span className={`wh-log-status ${isError ? "error" : "success"}`}>
                        {entry.status || "ERR"}
                      </span>
                      <span className="wh-log-integration">{entry.integrationName}</span>
                      <span className="wh-log-preview">{truncateJson(entry.body)}</span>
                      <span className="wh-log-time">{entry.responseTime}ms</span>
                      <span className="wh-log-ago">{timeAgo(entry.timestamp)}</span>
                      {entry.source === "test" && <span className="wh-log-badge">TEST</span>}
                      <span className="wh-log-expand">{isExpanded ? "▾" : "▸"}</span>
                    </div>
                    {isExpanded && (
                      <div className="wh-log-detail">
                        <div className="wh-log-detail-header">
                          <span>Full Payload — {new Date(entry.timestamp).toLocaleString()}</span>
                          <button className="wh-log-resend" onClick={() => resend(entry)}>Resend</button>
                        </div>
                        {entry.error && (
                          <div className="wh-log-error">Error: {entry.error}</div>
                        )}
                        <pre className="wh-log-json">{JSON.stringify(entry.body, null, 2)}</pre>
                        {entry.headers && (
                          <div className="wh-log-headers">
                            <strong>Headers:</strong> {JSON.stringify(entry.headers)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
